// ============================================================================
// spawns.js — the battle director. Keeps ~TARGET_ONSCREEN Magmar near Benjen
// (ring-spawned just beyond the action so they march into view), streams allied
// troops so the field fights without the player, guards bastions with pyre-
// captains, and resolves bastion capture (banner swap, war-horn, gate break,
// morale swing). Counting is throttled and hash-scoped — never per-frame O(n^2).
// ============================================================================

import {
  UNIT_FOOTSOLDIER, UNIT_ARCHER, UNIT_CAPTAIN, UNIT_WARLORD, TEAM_PLAYER, TEAM_MAGMAR,
  ST_DEAD, ST_ATTACK, MAP_HALF, TARGET_ONSCREEN, MAX_ENEMIES,
} from '../core/constants.js';
import { dist2 } from '../core/math.js';
import { EV } from '../core/events.js';

const NEAR_R = 72;                       // "on-screen-ish" radius around Benjen
const DESIRED_MAGMAR = TARGET_ONSCREEN;  // magmar to keep near the action (240)
const DESIRED_ALLY = 135;                // allies to keep in the fight nearby
const HARD_CAP = MAX_ENEMIES - 40;
const SOFT_CAP = 760;                    // cull far idle grunts beyond this

const CAPTURE_INTERVAL = 0.5;   // how often the bastion director ticks (s)
let _countT = 0, _captureT = 0;

function rollMagmarType(world) {
  const r = world.rng();
  if (r < 0.86) return UNIT_FOOTSOLDIER;
  if (r < 0.96) return UNIT_ARCHER;
  return UNIT_CAPTAIN;
}
function rollAllyType(world) {
  const r = world.rng();
  return r < 0.9 ? UNIT_FOOTSOLDIER : UNIT_ARCHER;
}

function ringSpawn(world, team, type, cx, cz, rMin, rMax) {
  if (world.units.count >= HARD_CAP) return null;
  const a = world.rng() * Math.PI * 2;
  const d = rMin + world.rng() * (rMax - rMin);
  let x = cx + Math.cos(a) * d, z = cz + Math.sin(a) * d;
  const lim = MAP_HALF - 4;
  x = Math.max(-lim, Math.min(lim, x));
  z = Math.max(-lim, Math.min(lim, z));
  return world.spawnUnit(team, type, x, z);
}

function countNear(world, x, z, r, team) {
  let n = 0;
  world.hash.forEachInRadius(x, z, r, (slot) => {
    const u = world.units.get(slot);
    if (u._active && u.team === team && u.state !== ST_DEAD) n++;
  });
  return n;
}

function spawnCaptain(world, bastion) {
  const u = world.spawnUnit(bastion.team, UNIT_CAPTAIN, bastion.x, bastion.z + 2);
  if (u) { u.homeX = bastion.x; u.homeZ = bastion.z; bastion.captainSlot = u._slot; }
  return u;
}

function captureBastion(world, bastion, newTeam) {
  bastion.team = newTeam;
  bastion.captainSlot = -1;
  bastion.respawnT = 3.5;
  if (world.battlefield) world.battlefield.recolorBastion(bastion);
  world.events.emit(EV.BASTION_CAPTURED, { bastion, team: newTeam });
  const who = newTeam === TEAM_PLAYER ? 'The Ashen Host seizes' : 'The Magmar reclaim';
  world.events.emit(EV.ANNOUNCE, { text: `${who} ${bastion.name}!`, tone: newTeam === TEAM_PLAYER ? 'good' : 'bad' });
  world.events.emit(EV.OBJECTIVE, { text: newTeam === TEAM_PLAYER ? `Hold ${bastion.name}. Press toward the citadel.` : `Retake ${bastion.name}.` });

  // breach the citadel gates as the forward bastions fall to the player
  if (newTeam === TEAM_PLAYER && world.battlefield) {
    if (bastion.id === 0 && world.battlefield.breakGate(0)) world.events.emit(EV.ANNOUNCE, { text: 'The West Gate is broken!', tone: 'good' });
    if (bastion.id === 1 && world.battlefield.breakGate(1)) world.events.emit(EV.ANNOUNCE, { text: 'The East Gate is broken!', tone: 'good' });
  }
  // capturer gets a small reinforcement burst
  for (let i = 0; i < 10; i++) ringSpawn(world, newTeam, rollMagmarType(world), bastion.x, bastion.z, 6, 16);
}

function updateBastions(world, dt) {
  const p = world.player;
  for (let i = 0; i < world.bastions.length; i++) {
    const b = world.bastions[i];
    b.respawnT -= dt;
    // validate captain
    let cap = b.captainSlot >= 0 ? world.units.get(b.captainSlot) : null;
    const capAlive = cap && cap._active && cap.state !== ST_DEAD && cap.team === b.team && cap.type === UNIT_CAPTAIN;
    if (!capAlive) b.captainSlot = -1;

    const owner = b.team;
    const foeTeam = 1 - owner;
    const defend = countNear(world, b.x, b.z, b.radius, owner);
    let assault = countNear(world, b.x, b.z, b.radius, foeTeam);
    // Benjen is worth a squad when contesting a Magmar bastion
    if (foeTeam === TEAM_PLAYER && p.state !== 'dead' && dist2(p.x, p.z, b.x, b.z) < b.radius * b.radius) assault += 6;

    if (!capAlive) {
      // respawnT doubles as a post-capture minimum-hold cooldown so a contested
      // captain-less bastion can't thrash ownership every director tick.
      if (b.respawnT <= 0 && assault > 0 && assault > defend) {
        captureBastion(world, b, foeTeam);
      } else if (b.respawnT <= 0) {
        spawnCaptain(world, b);
        b.respawnT = 10;
        // owner posts a few guards
        for (let k = 0; k < 6; k++) ringSpawn(world, owner, rollMagmarType(world), b.x, b.z, 4, 12);
      }
    }
  }
}

export function updateSpawns(world, dt) {
  const p = world.player;
  if (world.gameOver) return;
  _captureT -= dt;
  // Pass the director INTERVAL (not the sim tick) so bastion timers advance in
  // real seconds — updateBastions only runs every CAPTURE_INTERVAL.
  if (_captureT <= 0) { _captureT = CAPTURE_INTERVAL; updateBastions(world, CAPTURE_INTERVAL); }

  _countT -= dt;
  if (_countT > 0) return;
  _countT = 0.35;
  if (p.state === 'dead') return;

  const magmarNear = countNear(world, p.x, p.z, NEAR_R, TEAM_MAGMAR);
  const allyNear = countNear(world, p.x, p.z, NEAR_R, TEAM_PLAYER);

  // desired densities are per-level (world.mods), so difficulty can scale the horde.
  // A peaceful "walk your lands" ride has no enemy at all.
  const desiredMagmar = world.peaceful ? 0 : (world.mods.magmarNear || DESIRED_MAGMAR);
  const desiredAlly = world.mods.allyNear || DESIRED_ALLY;
  // top up Magmar around the player (they surround & press)
  if (magmarNear < desiredMagmar) {
    const batch = Math.min(24, desiredMagmar - magmarNear);
    for (let i = 0; i < batch; i++) ringSpawn(world, TEAM_MAGMAR, rollMagmarType(world), p.x, p.z, 40, 66);
  }
  // keep allies in the fray (spawn a touch faster so they aren't ground away)
  if (allyNear < desiredAlly) {
    const batch = Math.min(18, desiredAlly - allyNear);
    for (let i = 0; i < batch; i++) ringSpawn(world, TEAM_PLAYER, rollAllyType(world), p.x, p.z, 22, 54);
  }

  // standing bodyguards keep the leaders defended
  guardLeader(world, world.warmaster, TEAM_PLAYER, 18);
  guardLeader(world, world.dynast, TEAM_MAGMAR, 22);

  // bound total live units: recycle far, idle grunts that drifted off-screen
  cullFarGrunts(world);
}

function cullFarGrunts(world) {
  let over = world.units.count - SOFT_CAP;
  if (over <= 0) return;
  const p = world.player;
  const arr = world.units.active;
  for (let i = arr.length - 1; i >= 0 && over > 0; i--) {
    const u = arr[i];
    if (u.type === UNIT_WARLORD || u.type === UNIT_CAPTAIN) continue;
    if (u.state === ST_DEAD || u.state === ST_ATTACK) continue;
    if (dist2(u.x, u.z, p.x, p.z) < 110 * 110) continue; // keep anything on-screen-ish
    world.units.despawn(u);
    over--;
  }
}

function guardLeader(world, leader, team, minGuards) {
  if (!leader || !leader._active || leader.state === ST_DEAD) return;
  const g = countNear(world, leader.x, leader.z, 28, team);
  if (g < minGuards) {
    for (let i = 0; i < 8; i++) ringSpawn(world, team, rollMagmarType(world), leader.x, leader.z, 5, 15);
  }
}

// initial armies + bastion captains at battle start (density scales per level)
export function spawnInitialArmies(world) {
  for (let i = 0; i < world.bastions.length; i++) spawnCaptain(world, world.bastions[i]);
  const p = world.player;
  const s = world.mods.spawn || 1;
  if (world.peaceful) {
    // free-roam: only your own troops mill about your land, no enemy
    const ally = Math.round(70 * s);
    for (let i = 0; i < ally; i++) ringSpawn(world, TEAM_PLAYER, rollAllyType(world), p.x, p.z, 8, 48);
    for (let i = 0; i < 40; i++) {
      const x = (world.rng() - 0.5) * 280, z = (world.rng() - 0.5) * 280;
      ringSpawn(world, TEAM_PLAYER, rollAllyType(world), x, z, 0, 8);
    }
    return;
  }
  const mag = Math.round(120 * s), ally = Math.round(120 * s), scatter = Math.round(90 * s);
  // opening clash around Benjen — allies close in tight, Magmar press from the front.
  for (let i = 0; i < mag; i++) ringSpawn(world, TEAM_MAGMAR, rollMagmarType(world), p.x, p.z, 26, 62);
  for (let i = 0; i < ally; i++) ringSpawn(world, TEAM_PLAYER, rollAllyType(world), p.x, p.z, 8, 40);
  // scattered skirmishes across the field so it feels like a war
  for (let i = 0; i < scatter; i++) {
    const x = (world.rng() - 0.5) * 300, z = (world.rng() - 0.5) * 300;
    ringSpawn(world, world.rng() < 0.55 ? TEAM_MAGMAR : TEAM_PLAYER, rollMagmarType(world), x, z, 0, 8);
  }
}
